import { useEffect } from 'react';
import { ApolloError } from '@apollo/client';
import { getDutchiePayErrorMessage, getCreditErrorMessage, dutchiePayReasonCodes } from '@dutchie/error-messaging';
import { ERNIE_TIMEOUTS, ERNIE_TYPES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';
import { ERROR_TYPES, LOG_NAMESPACES, PBB_NAME } from 'src/payments/constants';
import { useDutchiePay } from 'src/payments/hooks';
import { tracker } from 'utils/analytics';
import { getLogger } from 'src/utils/datadog-logger';
import { useFlags } from 'launchdarkly-react-client-sdk';

const logger = getLogger(LOG_NAMESPACES.PBB);

export const UNLINKED_ACCOUNT_ERROR =
  'Your bank account is unlinked. Relink or change your bank account to finalize checkout. B105';

export const ENHANCED_ERROR_FLAG = 'fintech.retail-payments.pbb-enhanced-error-messages.ecomm-rollout';

type FormatPaymentsErrorOptions = {
  enhancedErrorsEnabled?: boolean;
  surface?: 'kiosk' | 'mobile' | 'web';
};

function formatPaymentsError(
  error: ApolloError | undefined,
  name: string | undefined,
  paymentMethod = ERROR_TYPES.DUTCHIE_PAY,
  options?: FormatPaymentsErrorOptions
): any {
  const extensions = error?.graphQLErrors?.[0]?.extensions;
  const reasonCode = extensions?.reason_code as string | undefined;
  const consumerMessage = extensions?.consumer_message as string | undefined;
  const posMessage = extensions?.pos_message as string | undefined;
  const reasonCodeMarkdown = reasonCode ? ` **${String(reasonCode)}**` : ``;

  // Detect if name is null or an empty string
  if (!name || name.trim() === '') {
    name = PBB_NAME;
  }

  let errorMessage: string;
  const useEnhanced = !!(options?.enhancedErrorsEnabled && consumerMessage);

  if (useEnhanced && consumerMessage) {
    // Use API-provided message; prefer pos_message for mobile surface
    errorMessage = options?.surface === 'mobile' && posMessage ? posMessage : consumerMessage;
  } else {
    // Existing behavior: static client-side mapping
    const errorMessageFormatter =
      paymentMethod === 'credit'
        ? getCreditErrorMessage(reasonCode)
        : getDutchiePayErrorMessage(reasonCode, undefined, name);
    errorMessage = errorMessageFormatter.ecomm;
  }

  const ernieType = ERNIE_TYPES.DANGER;
  const ernieTimeout = ERNIE_TIMEOUTS.LONG;
  const errorMarkdown = useEnhanced ? errorMessage : `${String(errorMessage)}${reasonCodeMarkdown}`;

  tracker.paymentsError({
    reasonCode: reasonCode && dutchiePayReasonCodes.includes(reasonCode) ? reasonCode : 'REASON_CODE_INVALID',
    meta: extensions ?? null,
    surface: options?.surface,
    enhancedMessages: useEnhanced,
  });

  logger.error('Payments error', {
    errorMessage,
    ernieType,
    reasonCode: reasonCode && dutchiePayReasonCodes.includes(reasonCode) ? reasonCode : 'REASON_CODE_INVALID',
    meta: extensions ?? null,
    useEnhanced,
  });

  return { errorMarkdown, errorMessage, ernieType, ernieTimeout, reasonCode };
}

function usePaymentsError(error: ApolloError | undefined): void {
  const showErnie = useErnie();
  const { whiteLabelConfig } = useDutchiePay();
  const flags = useFlags();
  const enhancedErrorsEnabled = !!flags[ENHANCED_ERROR_FLAG];

  useEffect(() => {
    if (error) {
      const { errorMarkdown, ernieType, ernieTimeout } = formatPaymentsError(
        error,
        whiteLabelConfig.brandedDutchiePayName,
        ERROR_TYPES.DUTCHIE_PAY,
        { enhancedErrorsEnabled, surface: 'web' }
      );
      showErnie(errorMarkdown, ernieType, ernieTimeout);
    }
  }, [error, showErnie, whiteLabelConfig.brandedDutchiePayName, enhancedErrorsEnabled]);
}

export function getGraphQLReasonCode(err: unknown): string | undefined {
  return (err as ApolloError)?.graphQLErrors?.[0]?.extensions?.reason_code as string | undefined;
}

export { usePaymentsError, formatPaymentsError };
